import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { API_URL } from "../../../../config";
import BcValueCircle from "./BcValueCircle";
import FilterableTable from "./FilterableTable";

// "Inventory Over 90" widget — green circle showing the total cost value of
// remaining inventory aged over 90 days (excluding the Commercial cost center),
// read live from Business Central. Firm-wide metric (not salesperson-scoped).
//
// Clicking the circle opens a modal with the per-item breakdown
// (GET /Dashboard/api/Dashboard/InventoryOver90Detail), filterable per column.
//
// Columns describe the stock item, where it sits, who it was brought in for, and
// how long it has been on hand. "Age" is derived here (today − postingDate) so it
// stays current without the API re-computing it.
//
// ASSUMED PAYLOAD KEYS — the API already returns these fields; the keys below
// follow the codebase's camelCase convention. Adjust any that don't match the
// actual InventoryOver90Detail response:
//   productStructure, storeName, privateName, manufacturer, style, color,
//   currentCost (BC unit cost), qtyAvailableToSell, salesPersonName, postingDate
const COLUMNS = [
  { key: "itemNo", label: "Item No" },
  // Derived below from postingDate (the day it was received).
  { key: "age", label: "Age (Days)", type: "number" },
  { key: "productStructure", label: "Product Structure", type: "text" },
  { key: "locationCode", label: "Store Name", type: "select" },
  { key: "productName", label: "Private Name" },
  { key: "manufacturer", label: "Manufacturer", type: "text" },
  { key: "style", label: "Style", type: "text" },
  { key: "color", label: "Color", type: "text" },
  { key: "costAmountActual", label: "Current Cost", type: "currency" },
  { key: "remainingQuantity", label: "QTY Available to Sell", type: "number" },
  // Salesperson the stock was brought in for (blank when unset in BC).
  { key: "salesPersonName", label: "Salesperson" },
];

// Whole calendar days between the received (posting) date and today. Compared on
// the date only — the API returns midnight timestamps, so keeping the time would
// let a same-day receipt read as -0/round oddly across DST.
const ageInDays = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  const ms =
    new Date(today.getFullYear(), today.getMonth(), today.getDate()) -
    new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.max(0, Math.floor(ms / 86400000));
};

const InventoryOver90 = ({ auth: { auth } }) => {
  const [value, setValue] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");

  const token = auth && auth[0] ? auth[0].token : null;

  // Add the derived Age column so it filters and exports like a real field.
  const rows = useMemo(
    () => (detail || []).map((r) => ({ ...r, age: ageInDays(r.postingDate) })),
    [detail]
  );

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/InventoryOver90`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setValue(rows[0] ? rows[0].value : 0);
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Inventory Over 90:", err.message);
      });
    return () => {
      active = false;
    };
  }, [token]);

  // Fetch the per-item breakdown lazily, the first time the modal opens.
  const openDetails = () => {
    setShowDetails(true);
    if (detailStatus !== "idle" || !token) return;
    setDetailStatus("loading");
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/InventoryOver90Detail`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setDetail(rows);
        setDetailStatus("ok");
      })
      .catch((err) => {
        console.warn("Could not load Inventory Over 90 detail:", err.message);
        setDetailStatus("error");
      });
  };

  return (
    <>
      <BcValueCircle
        title="Inventory Over 90"
        value={value}
        onClick={openDetails}
      />

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="ft-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Inventory Over 90</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailStatus === "loading" ? (
            <div className="ft-status">Loading…</div>
          ) : detailStatus === "error" ? (
            <div className="ft-status">
              Could not load the Inventory Over 90 details.
            </div>
          ) : (
            <FilterableTable
              columns={COLUMNS}
              rows={rows}
              emptyText="No inventory is over 90 days old."
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(InventoryOver90);
